import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import './App.css'
import App from './App.jsx'

// ─── Mobile: Prevent pinch-to-zoom & double-tap zoom ───
if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
  // Block iOS gesturestart (pinch-zoom)
  document.addEventListener('gesturestart', (e) => e.preventDefault(), { passive: false });
  document.addEventListener('gesturechange', (e) => e.preventDefault(), { passive: false });
  document.addEventListener('gestureend', (e) => e.preventDefault(), { passive: false });

  // Block multi-touch zoom
  document.addEventListener('touchstart', (e) => {
    if (e.touches.length > 1) e.preventDefault();
  }, { passive: false });

  // Block double-tap zoom
  let lastTouchEnd = 0;
  document.addEventListener('touchend', (e) => {
    const now = Date.now();
    if (now - lastTouchEnd <= 300) e.preventDefault();
    lastTouchEnd = now;
  }, { passive: false });
}

// ─── iOS Safari: точная высота видимой области (без тулбара Safari) ───
// На iOS Safari при первой загрузке visualViewport.height возвращает
// неправильную (полную) высоту. Правильное значение — только после resize.
// Решение: micro-scroll + отложенные пересчёты.

// Флаг: поле ввода сейчас в фокусе (пользователь печатает) — не сбрасывать скролл
let _inputFocused = false;

function updateAppHeight() {
  const vvh = window.visualViewport?.height;
  const ih = window.innerHeight;
  const ch = document.documentElement.clientHeight;
  // Берём минимальное значение — оно ближе всего к реальной видимой области
  const h = Math.min(vvh || Infinity, ih, ch);
  document.documentElement.style.setProperty('--app-height', `${h}px`);

  // Детектор клавиатуры на мобильных
  const isKeyboardOpen = window.visualViewport && (window.visualViewport.height < window.innerHeight - 150);
  if (isKeyboardOpen) {
    document.body.classList.add('keyboard-open');
  } else {
    document.body.classList.remove('keyboard-open');
  }

  // Возвращаем window в (0,0) на iOS при изменении visualViewport,
  // НО ТОЛЬКО если пользователь НЕ печатает в поле ввода.
  // Иначе этот scrollTo(0,0) постоянно сбрасывает позицию и поле «уезжает» за клавиатуру.
  if (!_inputFocused && window.visualViewport && (/iPhone|iPad|iPod/.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))) {
    if (window.scrollY !== 0 || window.scrollX !== 0) {
      window.scrollTo(0, 0);
    }
  }
}

if (window.visualViewport) {
  window.visualViewport.addEventListener('resize', updateAppHeight);
  window.visualViewport.addEventListener('scroll', updateAppHeight);
}
window.addEventListener('resize', updateAppHeight);
window.addEventListener('orientationchange', () => {
  setTimeout(updateAppHeight, 100);
  setTimeout(updateAppHeight, 300);
});

// Первый запуск
updateAppHeight();

// Серия отложенных пересчётов — ловим момент когда Safari пересчитает viewport
requestAnimationFrame(() => {
  updateAppHeight();
  requestAnimationFrame(updateAppHeight);
});
setTimeout(updateAppHeight, 50);
setTimeout(updateAppHeight, 150);
setTimeout(updateAppHeight, 300);
setTimeout(updateAppHeight, 500);

// После полной загрузки страницы (шрифты, изображения)
window.addEventListener('load', () => {
  updateAppHeight();
  setTimeout(updateAppHeight, 100);
  setTimeout(updateAppHeight, 300);
  // Micro-scroll трюк — заставляет iOS Safari пересчитать viewport
  if (/iPhone|iPad|iPod/.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    window.scrollTo(0, 1);
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      updateAppHeight();
    });
  }
});

// ─── iOS: Обеспечиваем видимость поля ввода над клавиатурой ───
// Проблема: iOS Safari при открытии клавиатуры сдвигает viewport, но
// fixed-layout контейнеры (как чат) не прокручиваются автоматически.
// updateAppHeight() сбрасывает window.scrollTo(0,0) при каждом resize,
// что мешает scrollIntoView. Решение: блокируем scrollTo(0,0) пока
// поле в фокусе, и скроллим ближайший scrollable-родитель, а не window.
if (/iPhone|iPad|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
  
  // Найти ближайший скроллируемый контейнер-родитель (для fixed-layout страниц)
  function findScrollableParent(el) {
    let parent = el?.parentElement;
    while (parent && parent !== document.body) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  const handleFocusIn = (e) => {
    if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) {
      _inputFocused = true;

      // Ждём завершения анимации клавиатуры iOS (~400ms), затем прокручиваем
      setTimeout(() => {
        if (!_inputFocused) return; // Пользователь уже вышел из фокуса

        updateAppHeight();

        // Для fixed-layout (чат) — ищем внутренний scrollable-контейнер
        // и прокручиваем сообщения вниз, чтобы поле ввода было видно
        const scrollableParent = findScrollableParent(e.target);
        if (scrollableParent) {
          // Если поле ввода внутри fixed-контейнера, скроллим родителя вниз
          scrollableParent.scrollTop = scrollableParent.scrollHeight;
        }

        // Дополнительно: стандартный scrollIntoView как fallback для обычных страниц
        // (не-fixed контейнеры, модалки, настройки и т.д.)
        try {
          e.target.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } catch (_) { /* ignore */ }
      }, 400);
    }
  };

  const handleFocusOut = (e) => {
    // Проверяем что новый фокус не попал в другое поле ввода
    // (если пользователь переключается между полями — не сбрасываем)
    setTimeout(() => {
      const active = document.activeElement;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
        return; // Фокус перешел в другое поле — не трогаем
      }
      _inputFocused = false;
      // При выходе из поля ввода возвращаем экран в исходное положение
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      updateAppHeight();
    }, 150);
  };

  document.addEventListener('focusin', handleFocusIn);
  document.addEventListener('focusout', handleFocusOut);
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
